import { useCallback, useEffect, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import moment from 'moment'
import useDayOfWeek from '../../common/components/DayOfWeek/hooks/useDayOfWeek'
import {
  useCheckin,
  useDeviceWidth,
  useScrolledToRefBottom,
  useSessionStorage,
} from '../../common/hooks'
import useThrottle from '../../common/hooks/useThrottle'
import { parseCheckInError } from '../../common/networking/checkins/logic/checkinError'
import { workoutsApi } from '../../common/networking/workouts'
import {
  analytics,
  analyticsEvent,
  analyticsEventLabel,
} from '../../common/utility/analytics'
import { trackFavoriteEvents } from '../../common/utility/commonAnalytics'
import { getHhMmFromUtcDate } from '../../common/utility/datetimes'
import { testIdAttribute, testIds } from '../../common/utility/labels'
import { postLiveCheckInCompleted } from '../../common/utility/mobileAppBridge'
import configuration from '../../configuration'
import { useMobileAppAccess } from '../../context/MobileApp'
import { authSelector, redirectToPath } from '../../redux/features/Auth'
import { AuthToken } from '../../redux/features/Auth/auth'
import {
  favorite,
  favoriteSelector,
} from '../../redux/features/OnDemand/Favorite'
import {
  filtersReset,
  filtersSelector,
  getFilters,
  setFavorite,
  setFilters,
  setTime,
} from '../../redux/features/OnDemand/Filters'
import {
  getWorkouts,
  parseResponseByFilterType,
  workoutsReset,
  workoutsSelector,
} from '../../redux/features/OnDemand/Workouts'
import {
  resetError,
  virtualCheckInSelector,
} from '../../redux/features/Virtual/CheckIn'
import {
  selectedVirtualWorkoutReset,
  selectedVirtualWorkoutSelector,
  setVirtualWorkoutSuccess,
} from '../../redux/features/Virtual/SelectedWorkout'
import { useAppDispatch, useAppSelector } from '../../redux/store'
import { JoinLiveClickArgs } from '../OnDemandWorkouts/OnDemandWorkouts.d'

const { trackCustomEvent } = analytics
const { BUTTON_CLICK, SCREEN_CHANGE } = analyticsEvent
const {
  COMMON: { FAVORITES },
  WORKOUT_DETAILS: { JOIN_LIVE },
} = analyticsEventLabel

const useVirtualWorkouts = () => {
  const [isPageInitialized, setIsPageInitialized] = useState<boolean>(false)
  const [hasJoinedWorkout, setHasJoinedWorkout] = useState<boolean>(false)
  const [isOverlayOn, setIsOverlayOn] = useState(false)
  const deviceWidth = useDeviceWidth()
  const dispatch = useAppDispatch()
  const location = useLocation()
  const history = useHistory()
  const workoutsGridRef = useRef<HTMLDivElement>(null)
  const [filtersApplied, setFiltersApplied] = useState<boolean>(false)
  const [isGrowlVisible, setIsGrowlVisible] = useState<boolean>(false)
  const [filteredNumberOfWorkouts, setFilteredNumberOfWorkouts] =
    useState<number>(0)
  const [trainerId, setTrainerId] = useState<string>('')
  const [isSliderFilterLoading, setIsSliderFilterLoading] =
    useState<boolean>(false)
  const virtualWorkoutsPath = window.location.pathname === '/virtual-workouts'
  const automaticCheckInAttemptedRef = useRef<Set<number>>(new Set())
  const pendingLiveCelebrationsRef = useRef<Set<number>>(new Set())
  const lastWorkoutsRequestKeyRef = useRef<string>()
  const filtersRequestedRef = useRef<boolean>(false)
  const screenChangeTrackedRef = useRef<boolean>(false)

  const { createCheckin } = useCheckin()
  const throttleFunction = useThrottle()

  const pageTitle = 'Live Workouts'

  const workoutsGridLoadNewPageOffsetInPx = -100

  const { workoutAnticipationMinutes } = configuration

  const scrolledToWorkoutsGridBottom = useScrolledToRefBottom(
    workoutsGridRef,
    workoutsGridLoadNewPageOffsetInPx,
  )
  const { token, user } = useAppSelector(authSelector)
  const {
    timesFilter: { startTime, endTime },
    tagFilters: tagFiltersApplied,
    favoriteFilter: isFavoriteFilterApplied,
    hideCompletedFilter: isHideCompletedFilterApplied,
  } = useAppSelector(filtersSelector)

  const [
    isFavoriteFilterAppliedFromStorage,
    setIsFavoriteFilterAppliedFromStorage,
  ] = useSessionStorage('isFavoriteFilterApplied', false)
  const [, , removeFilterValues] = useSessionStorage('filterValues')

  const {
    isLoading: isLoadingWorkouts,
    lastPageFetched,
    totalNumberOfPages,
    totalNumberOfWorkouts,
    workouts,
  } = useAppSelector(workoutsSelector)

  const { selectedVirtualWorkout } = useAppSelector(
    selectedVirtualWorkoutSelector,
  )
  const allFavorites = useAppSelector(favoriteSelector.selectEntities)
  const {
    isLoading: isCheckinLoading,
    error: checkinError,
    checkedInWorkout,
  } = useAppSelector(virtualCheckInSelector)

  const hasSessionExpired =
    parseCheckInError(checkinError) === 'Session Expired'
  const growlMessage = 'Something went wrong. Please try again.'

  const isMorePagesToFetch =
    (typeof lastPageFetched === 'undefined' ? -1 : lastPageFetched) <
    totalNumberOfPages - 1

  const onClearFilterByTimeClick = () => {
    setFiltersApplied(false)
    removeFilterValues()
    dispatch(workoutsReset())
    dispatch(filtersReset())
  }

  const onApplyFilterByTimeClick = (
    startTime: string,
    endTime: string,
    tagId: number | null,
  ) => {
    const startValue = getHhMmFromUtcDate(startTime)
    const endValue = getHhMmFromUtcDate(endTime)

    if (startValue === '00:00' && endValue === '23:59' && tagId === null) {
      onClearFilterByTimeClick()
    } else {
      dispatch(workoutsReset())
      dispatch(setTime(startTime, endTime))
      const tagFilter = tagId ? [tagId] : []
      dispatch(setFilters(tagFilter, false))
      setFiltersApplied(true)
    }
  }

  const { fetchWorkouts } = workoutsApi()

  const redirectToLogin = useCallback(
    () =>
      history.push(
        `/login?redirectUrl=${encodeURIComponent(window.location.href)}`,
      ),
    [history],
  )

  const onFilterByTimeSlide = async (
    startTime: string,
    endTime: string,
    areFiltersVisible: boolean,
    tags: number[],
  ) => {
    setIsSliderFilterLoading(true)
    const authToken = token
    const response = await fetchWorkouts({
      startTimeFilter: startTime,
      endTimeFilter: endTime,
      favoriteFilter: isFavoriteFilterApplied,
      hideCompletedFilter: false,
      tagFilters: tags,
      timeFilter: areFiltersVisible,
      trainerId,
      authToken,
    })
    const finalResponse = parseResponseByFilterType({
      response,
      isOnDemandFilter: false,
    })
    const total = isFavoriteFilterApplied
      ? finalResponse.content.length
      : finalResponse.totalElements
    setFilteredNumberOfWorkouts(total)
    setIsSliderFilterLoading(false)
  }

  const handleOnFavoriteClick = (workoutId: number, isFavorite: boolean) => {
    const favoriteParams = { workoutId, isFavorite }
    trackFavoriteEvents(isFavorite)
    dispatch(favorite(favoriteParams))
  }

  const handleOnGetFavoritesWorkouts = () => {
    dispatch(workoutsReset())
    dispatch(setFavorite(!isFavoriteFilterApplied))
    setIsFavoriteFilterAppliedFromStorage(!isFavoriteFilterAppliedFromStorage)
    trackCustomEvent(BUTTON_CLICK(FAVORITES))
  }

  const handleOnJoinLiveClick = async ({
    target,
    workout,
  }: JoinLiveClickArgs) => {
    trackCustomEvent(BUTTON_CLICK(JOIN_LIVE))

    if (!workout?.id) {
      return
    }

    const currentUrl = new URL(window.location.href)
    currentUrl.searchParams.set('checkin', workout.id.toString())
    currentUrl.searchParams.set('autoCheckin', 'true')
    const callbackUrl = currentUrl.toString()

    sessionStorage.setItem(`pendingCheckIn_${workout.id}`, 'true')
    sessionStorage.setItem(`checkInCallbackUrl_${workout.id}`, callbackUrl)
    if (!checkedInWorkout.includes(workout.id)) {
      pendingLiveCelebrationsRef.current.add(workout.id)
    }

    window.open(workout?.link, target)
    await dispatch(setVirtualWorkoutSuccess(workout))

    if (user?.id && token) {
      setTimeout(() => {
        handleAutomaticCheckIn(workout.id!)
      }, 2000)
    }
  }

  const handleOnLoginClick = () => {
    dispatch(redirectToPath(location.pathname))
    redirectToLogin()
  }

  const onCheckInClick = async (
    userId: string,
    workoutId: number,
    token: AuthToken,
  ) => {
    createCheckin({
      type: 'ONLINE',
      userId,
      workoutId,
      token,
    })
  }

  const handleAutomaticCheckIn = useCallback(
    (workoutId: number) => {
      if (automaticCheckInAttemptedRef.current.has(workoutId)) {
        return
      }

      if (!user?.id || !token) {
        return
      }

      const workout = workouts.find((w) => w.id === workoutId)
      if (!workout || !workout.startTime || !workout.duration) {
        return
      }

      const now = moment.utc()
      const checkInStartTime = moment
        .utc(workout.startTime)
        .subtract(workoutAnticipationMinutes, 'minutes')
      const workoutEndTime = moment
        .utc(workout.startTime)
        .add(workout.duration, 'minutes')

      const isCheckInWindowOpen = now.isBetween(
        checkInStartTime,
        workoutEndTime,
        null,
        '[]',
      )

      if (!isCheckInWindowOpen || !workout.isLive) {
        return
      }

      automaticCheckInAttemptedRef.current.add(workoutId)

      createCheckin({
        type: 'ONLINE',
        userId: user.id,
        workoutId,
        token,
      })
    },
    [user?.id, token, workouts, createCheckin, workoutAnticipationMinutes],
  )

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search)
    const checkinWorkoutId = urlParams.get('checkin')
    const shouldTriggerCheckin = urlParams.get('autoCheckin') === 'true'

    if (shouldTriggerCheckin && checkinWorkoutId) {
      const workoutId = parseInt(checkinWorkoutId, 10)
      if (!isNaN(workoutId)) {
        handleAutomaticCheckIn(workoutId)
        const newSearchParams = new URLSearchParams(location.search)
        newSearchParams.delete('checkin')
        newSearchParams.delete('autoCheckin')
        const newSearch = newSearchParams.toString()
        history.replace({
          pathname: location.pathname,
          search: newSearch ? `?${newSearch}` : '',
        })
      }
    }
  }, [location.search, history, location.pathname, handleAutomaticCheckIn])

  const onGrowlClose = () => {
    dispatch(resetError())
    setIsGrowlVisible(false)
  }

  const useDayOfWeekProps = useDayOfWeek({
    onClearFilterByTimeClick,
  })

  const { dayOfWeekItems } = useDayOfWeekProps

  const overlayTestId = testIdAttribute(testIds.WORKOUTS.OVERLAY)
  const currentDayOfWeekSelected = dayOfWeekItems.filter((day) => day.active)[0]
    .id

  const { hasMobileAppAccess } = useMobileAppAccess()

  useEffect(() => {
    if (!hasMobileAppAccess) return

    pendingLiveCelebrationsRef.current.forEach((workoutId) => {
      if (!checkedInWorkout.includes(workoutId)) return

      if (postLiveCheckInCompleted({ workoutId })) {
        pendingLiveCelebrationsRef.current.delete(workoutId)
      }
    })
  }, [checkedInWorkout, hasMobileAppAccess])

  const loadMoreWorkouts = useCallback(() => {
    !isLoadingWorkouts &&
      isMorePagesToFetch &&
      dispatch(
        getWorkouts({
          isOnDemandFilter: false,
          dayOfWeekFilter: currentDayOfWeekSelected,
          trainerId,
          hasMobileAppAccess,
        }),
      )
  }, [
    dispatch,
    isLoadingWorkouts,
    isMorePagesToFetch,
    currentDayOfWeekSelected,
    trainerId,
    hasMobileAppAccess,
  ])

  const isSelectedWorkoutLive = useCallback(() => {
    if (selectedVirtualWorkout?.startTime) {
      const dateStartLive = moment
        .utc(selectedVirtualWorkout.startTime)
        .subtract(workoutAnticipationMinutes, 'minutes')
      const dateEndLive = moment
        .utc(selectedVirtualWorkout.startTime)
        .add(selectedVirtualWorkout.duration, 'minutes')
      const usersCurrentTime = moment.utc()
      const isLive = usersCurrentTime.isBetween(dateStartLive, dateEndLive)
      return isLive
    } else {
      return false
    }
  }, [
    selectedVirtualWorkout.duration,
    selectedVirtualWorkout.startTime,
    workoutAnticipationMinutes,
  ])

  const isLoadingFavorite = (workoutId: number) =>
    (allFavorites &&
      allFavorites[workoutId] &&
      allFavorites[workoutId].isLoading) ||
    false

  const onClearTrainerSearch = () => {
    setTrainerId('')
  }

  const loadWorkouts = useCallback(() => {
    dispatch(workoutsReset())
    if (isFavoriteFilterAppliedFromStorage) {
      dispatch(setFavorite(true))
    }
    dispatch(
      getWorkouts({
        isOnDemandFilter: false,
        dayOfWeekFilter: currentDayOfWeekSelected,
        trainerId,
        hasMobileAppAccess,
      }),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    isHideCompletedFilterApplied,
    currentDayOfWeekSelected,
    filtersApplied,
    trainerId,
    isFavoriteFilterAppliedFromStorage,
    startTime,
    endTime,
    tagFiltersApplied,
    hasMobileAppAccess,
  ])

  useEffect(() => {
    if (typeof hasMobileAppAccess === 'undefined') return

    const requestKey = JSON.stringify({
      currentDayOfWeekSelected,
      filtersApplied,
      trainerId,
      isFavoriteFilterAppliedFromStorage,
      startTime,
      endTime,
      tagFiltersApplied,
      hasMobileAppAccess,
      isHideCompletedFilterApplied,
    })

    if (lastWorkoutsRequestKeyRef.current === requestKey) return

    lastWorkoutsRequestKeyRef.current = requestKey
    throttleFunction(loadWorkouts, 500)
  }, [
    currentDayOfWeekSelected,
    filtersApplied,
    trainerId,
    isFavoriteFilterAppliedFromStorage,
    startTime,
    endTime,
    tagFiltersApplied,
    hasMobileAppAccess,
    isHideCompletedFilterApplied,
    loadWorkouts,
    throttleFunction,
  ])

  useEffect(() => {
    if (filtersRequestedRef.current) return

    filtersRequestedRef.current = true
    dispatch(getFilters())
    setIsPageInitialized(true)
  }, [dispatch])

  useEffect(() => {
    scrolledToWorkoutsGridBottom && loadMoreWorkouts()
  }, [scrolledToWorkoutsGridBottom, loadMoreWorkouts])

  useEffect(() => {
    if (selectedVirtualWorkout && selectedVirtualWorkout?.startTime) {
      setHasJoinedWorkout(true)
    }
  }, [selectedVirtualWorkout])

  useEffect(() => {
    if (hasJoinedWorkout) {
      if (!isSelectedWorkoutLive()) {
        setHasJoinedWorkout(false)
        dispatch(selectedVirtualWorkoutReset())
      }
    }
  }, [dispatch, hasJoinedWorkout, isSelectedWorkoutLive])

  useEffect(() => {
    setIsGrowlVisible(!!checkinError)
  }, [checkinError])

  useEffect(() => {
    if (hasSessionExpired) {
      dispatch(resetError())
      dispatch(redirectToPath(location.pathname))
      redirectToLogin()
    }
  }, [hasSessionExpired, dispatch, history, location.pathname, redirectToLogin])

  useEffect(() => {
    if (screenChangeTrackedRef.current) return

    screenChangeTrackedRef.current = true
    trackCustomEvent(SCREEN_CHANGE())
  }, [])

  useEffect(() => {
    if (isFavoriteFilterApplied) {
      setIsFavoriteFilterAppliedFromStorage(true)
    }
  }, [isFavoriteFilterApplied, setIsFavoriteFilterAppliedFromStorage])

  useEffect(() => {
    if (virtualWorkoutsPath) {
      setIsFavoriteFilterAppliedFromStorage(false)
      dispatch(setFavorite(false))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [virtualWorkoutsPath])

  return {
    allFavorites,
    checkedInWorkout,
    checkinError,
    currentDayOfWeekSelected,
    deviceWidth,
    filteredNumberOfWorkouts,
    filtersApplied,
    growlMessage,
    hasJoinedWorkout,
    isCheckinLoading,
    isFavoriteFilterApplied,
    isGrowlVisible,
    isHideCompletedFilterApplied,
    isLoadingWorkouts,
    isOverlayOn,
    isPageInitialized,
    isSliderFilterLoading,
    overlayTestId,
    pageTitle,
    selectedVirtualWorkout,
    tagFiltersApplied,
    trainerId,
    totalNumberOfWorkouts,
    useDayOfWeekProps,
    virtualWorkoutsPath,
    workouts,
    workoutsGridRef,
    handleOnFavoriteClick,
    handleOnGetFavoritesWorkouts,
    handleOnJoinLiveClick,
    handleOnLoginClick,
    isLoadingFavorite,
    onApplyFilterByTimeClick,
    onCheckInClick,
    onClearFilterByTimeClick,
    onClearTrainerSearch,
    onFilterByTimeSlide,
    onGrowlClose,
    setIsOverlayOn,
    setTrainerId,
    handleAutomaticCheckIn,
  }
}

export default useVirtualWorkouts
